import { track, type TryOnEventParams } from '../analytics/events';
import { btn, h, icon } from '../ui/icons';
import { words } from '../ui/words';
import { a204words } from '../ui/a204words';
import { orderConsent, uploadDossier } from './privateDossier';
import { INITIAL_ORDER_FLOW, orderFlow, type OrderFlow, type PhotoHandOff } from './orderFlow';
import {
  addToCart, bandBaselines, cartProperties, coverRect, frameTitle, normaliseRoot, ORDER_PHOTO, orderCode, orderLines, photoCaption,
  photoFileName, photoText, productUrl, whatsappMessage, whatsappUrl, wrapItems,
  type OrderSnapshot, type PhotoText, type ShopContext,
  supplementProperties,
} from './orderSpecs';
import { composeOrderPdf, pdfFileName, renderOrderPage } from './orderPdf';
import { orderCopy } from './snapshot';
import { getLang } from '../ui/i18n';
import { isSunsetId, SIGNATURE_SUNSET_SUPPLEMENT_PRODUCT } from '../config/appearance';

/** What the panel needs from the try-on. Everything is read again at each use: the model can change. */
export interface OrderPanelHost {
  /** Current frame, Colours choice and Size selection. */
  snapshot(): OrderSnapshot;
  /** Camera + 3D composite from the render loop (mirrored like the screen); null when unavailable. */
  capture(): Promise<HTMLCanvasElement | null>;
  /** Centre of the tracked face in the last photo (fractions of the photo), when known. */
  focus?(): { x: number; y: number } | null;
  /** Camera stage: the 3-2-1 countdown is shown over it. */
  stage: HTMLElement;
  /** Shopify storefront when embedded, null on the standalone page. */
  shop: ShopContext | null;
  dossierUrl?: string;
  retentionDays?: number;
  workshopRequired?: () => boolean;
  /** A110Z: reopens the engraving (the Gravure line is clickable). */
  openEngrave?: () => void;
  /**
   * A110Z3: the engraved temples (photo in the chosen acetate, the texts where the customer placed them), drawn
   * under the photo band for the workshop; null while the engraving is the house one.
   */
  engravingSheet?: (family: string) => Promise<HTMLCanvasElement | null>;
  /** A201: page 2 of the order sheet (PDF) — the four faces of the temples, for a frame that can be engraved. */
  workshopPage?: (family: string, code: string) => Promise<HTMLCanvasElement | null>;
  /** A201: heavy work of ours in progress (the order sheet): the try-on's watchdog lets long frames pass. */
  busy?: (on: boolean) => void;
}

type PanelState = 'idle' | 'capturing' | 'countdown' | 'ready' | 'error';

/** Thumbnail drawing size (4:5, about 2× its CSS size). */
const THUMB_W = 240;
const THUMB_H = 300;
const COUNTDOWN_STEP_MS = 1000;
/** After a model change with the panel open: time for the new frame to be on the face before the new photo. */
const RECAPTURE_AFTER_SWITCH_MS = 900;
const FONT_FALLBACK = "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif";
const SPEC_SEPARATOR = '   |   ';

interface ShopWindow extends Window { Shopify?: { routes?: { root?: unknown } } }

/**
 * Shopify storefront the try-on runs in, detected like `main.ts`
 * (`Shopify.routes.root`). The standalone catalogue and the QA pages are never
 * a storefront, whatever the window holds.
 */
export function detectShop(): ShopContext | null {
  const root = (window as ShopWindow).Shopify?.routes?.root;
  if (typeof root !== 'string' || !root) return null;
  if (document.getElementById('mt-catalogue') || document.getElementById('mt-launch')) return null;
  return { origin: location.origin, root: normaliseRoot(root) };
}

type ShareNavigator = Navigator & { canShare?: (data?: ShareData) => boolean };

/** True when the system share sheet takes this file (`navigator.canShare({ files })`). */
function canShareFile(file: File): boolean {
  try {
    const nav = navigator as ShareNavigator;
    return typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** A201: the share sheet takes the photo and the order sheet together. */
function canShareFiles2(a: File, b: File): boolean {
  try {
    const nav = navigator as ShareNavigator;
    return typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [a, b] });
  } catch {
    return false;
  }
}

/** True when the system share sheet takes image files (phones); desktops download instead. */
function canShareFiles(): boolean {
  return canShareFile(new File([new Uint8Array([0xff, 0xd8, 0xff])], 'probe.jpg', { type: 'image/jpeg' }));
}

const errorName = (err: unknown): string | undefined => (err as { name?: string } | null)?.name;

/** Gives the frame back to the render loop (a timeout when the page is hidden: no frame comes). */
function nextFrame(): Promise<void> {
  return new Promise(resolve => {
    if (document.hidden) { window.setTimeout(resolve, 0); return; }
    requestAnimationFrame(() => window.setTimeout(resolve, 0));
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([promise, new Promise<undefined>(resolve => window.setTimeout(() => resolve(undefined), ms))]);
}

/** Waits for the try-on typeface in the weights the photo uses (never longer than 2 s). */
async function loadFonts(family: string): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  try {
    await withTimeout(Promise.all(['500 22px', '600 46px', '400 25px'].map(f => fonts.load(`${f} ${family}`))), 2000);
    await withTimeout(fonts.ready, 2000);
  } catch {
    // A font that cannot load leaves the system fallback of the family list.
  }
}

/** Centred text with letter spacing (drawn glyph by glyph: `ctx.letterSpacing` is not universal). */
function drawSpaced(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, spacing: number): void {
  const glyphs = Array.from(text);
  const widths = glyphs.map(g => ctx.measureText(g).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * Math.max(0, glyphs.length - 1);
  let x = cx - total / 2;
  ctx.textAlign = 'left';
  glyphs.forEach((g, i) => { ctx.fillText(g, x, y); x += widths[i]! + spacing; });
  ctx.textAlign = 'center';
}

/**
 * The try-on photo as a JPEG (1080 × 1350, quality 0.9): the photo on top
 * (1080 × 1080, cover crop around the face) and a 270 px band with the model
 * and its specs. Built on the device only.
 *
 * A110Z3: with a custom engraving, the sheet of engraved temples follows under the band (the JPEG grows by its
 * height), so the photo sent to Maison Mata shows the placement and the colour.
 */
export async function composeOrderPhoto(photo: HTMLCanvasElement, focus: { x: number; y: number } | null, text: PhotoText, family: string, sheet: Promise<HTMLCanvasElement | null> | null = null): Promise<Blob | null> {
  const P = ORDER_PHOTO;
  await loadFonts(family);
  const extra = sheet ? await sheet : null;
  if (sheet && !extra) throw new Error('Incomplete engraving photo');
  const canvas = document.createElement('canvas');
  canvas.width = P.width; canvas.height = P.height + (extra && extra.width === P.width ? extra.height : 0);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const crop = coverRect(photo.width, photo.height, P.photo, P.photo, focus ?? undefined);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, P.photo, P.photo);
  ctx.fillStyle = P.paper;
  ctx.fillRect(0, P.photo, P.width, P.band);

  const maxWidth = P.width - 2 * P.margin;
  const cx = P.width / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = P.ink;
  // Spec lines: at most two, the font steps down before the last line is squeezed.
  let specSize = 25;
  let lines: string[] = [];
  for (const size of [25, 23, 21]) {
    specSize = size;
    ctx.font = `400 ${size}px ${family}`;
    lines = wrapItems(text.specs, maxWidth, t => ctx.measureText(t).width, SPEC_SEPARATOR);
    if (lines.length <= 2) break;
  }
  if (lines.length > 2) lines = [lines[0]!, lines.slice(1).join(SPEC_SEPARATOR)];
  const base = bandBaselines(lines.length);
  const top = P.photo;
  ctx.font = `500 22px ${family}`;
  drawSpaced(ctx, text.brand, cx, top + base.brand, 7);
  ctx.font = `600 46px ${family}`;
  ctx.fillText(text.title, cx, top + base.title, maxWidth);
  ctx.font = `400 ${specSize}px ${family}`;
  lines.forEach((line, i) => ctx.fillText(line, cx, top + (base.specs[i] ?? base.specs[0]!), maxWidth));
  if (text.url) {
    ctx.font = `400 21px ${family}`;
    ctx.fillStyle = P.muted;
    ctx.fillText(text.url, cx, top + base.url, maxWidth);
  }
  if (extra) {
    if (extra.width === P.width) ctx.drawImage(extra, 0, P.height);
    extra.width = 0; extra.height = 0;
  }
  return new Promise(resolve => canvas.toBlob(blob => {
    // A110Y: a 1080×1350 canvas per photo was left to the garbage collector; released now.
    canvas.width = 0;
    canvas.height = 0;
    resolve(blob);
  }, 'image/jpeg', P.quality));
}

/** A110Y: releases a photo canvas once no JPEG is being composed from it. */
function releaseCanvas(canvas: HTMLCanvasElement | null, after?: Promise<unknown>): void {
  if (!canvas) return;
  const release = (): void => {
    canvas.width = 0;
    canvas.height = 0;
  };
  if (after) void after.then(release, release);
  else release();
}

/**
 * A110V « Je veux cette paire ». Opening the panel takes a photo of the
 * customer wearing the frame (the live composite) and lists every choice
 * (model, acetate, lenses, size, PD), with three actions: WhatsApp, save or
 * share the photo, add to the Shopify cart (or see the product on
 * maisonmata.com outside Shopify). The photo never leaves the device except
 * through the customer's own share or download.
 *
 * A110W: WhatsApp in two steps (a wa.me link cannot carry a picture). Once
 * « Commander sur WhatsApp » has opened the chat, step 1 shows as done (with
 * « Rouvrir ») and step 2 becomes the primary button: « Envoyer la photo à
 * Maison Mata » opens the share sheet with the JPEG prepared after the capture
 * (within the tap, for iOS), or « Enregistrer la photo » downloads it where
 * files cannot be shared. See `orderFlow.ts` for when step 1 resets.
 */
export class OrderPanel {
  readonly root = h('div', 'mt-view mt-order-view');
  private readonly thumb = h('canvas', 'mt-order-photo');
  private readonly retake = btn('mt-order-retake');
  private readonly specs = h('dl', 'mt-order-specs');
  private readonly wa = h('a', 'mt-cta mt-order-wa');
  private readonly hint = h('p', 'mt-order-hint');
  /** A110W step 2: « ✓ Message préparé dans WhatsApp · Rouvrir », the send button and its help line. */
  private readonly done = h('div', 'mt-order-done');
  private readonly reopen = h('a', 'mt-link mt-order-reopen');
  private readonly send = btn('mt-cta mt-order-send');
  private readonly sendLabel = h('span', 'mt-cta-text');
  private readonly help = h('p', 'mt-order-help');
  private readonly save = btn('mt-cta-secondary mt-order-save');
  private readonly saveLabel = h('span', 'mt-order-label');
  private readonly cart: HTMLButtonElement | null;
  private readonly cartLabel = h('span', 'mt-order-label');
  private readonly cartDone: HTMLAnchorElement | null;
  private readonly site: HTMLAnchorElement | null;
  private readonly message = h('p', 'mt-order-msg');
  private readonly status = h('p', 'mt-order-sr');
  /** A110Z: « Prix et délai de gravure : à confirmer », last line of the specs while an engraving is on. */
  private readonly priceNote = h('div', 'mt-order-price');
  /** A201: « Code commande · MM-0910-K7Q4F » under the choices; the order sheet (PDF) button. */
  private readonly codeLine = h('div', 'mt-order-code');
  private readonly codeText = h('span', 'mt-order-code-text');
  private readonly pdfButton = btn('mt-link mt-order-pdf');
  /** The visit's part of the order code (the same choices keep their code during the visit). */
  private readonly salt = Math.random().toString(36).slice(2, 10);
  /** The order sheet of `photo` for the choices in `key` (same cache rules as the JPEG). */
  private pdf: { key: string; photo: HTMLCanvasElement; promise: Promise<File | null>; ready: File | null; done: boolean } | null = null;
  private messageKind: 'photo' | 'cart' | null = null;
  private photo: HTMLCanvasElement | null = null;
  private photoFocus: { x: number; y: number } | null = null;
  /** JPEG of `photo` for the choices in `key`; `ready` once composed (null and `done` when it failed). */
  private file: { key: string; photo: HTMLCanvasElement; promise: Promise<File | null>; ready: File | null; done: boolean } | null = null;
  private visible = false;
  private captureToken = 0;
  private countdownTimer = 0;
  private countdownEl: HTMLElement | null = null;
  private recaptureTimer = 0;
  /** Handle of the product the « Ajoutée » state belongs to. */
  private addedHandle: string | null = null;
  private addedPhoto: HTMLCanvasElement | null = null;
  private consentDialog: ReturnType<typeof orderConsent> | null = null;
  private readonly uploadAbort = new AbortController();
  private dossier: { key: string; photo: HTMLCanvasElement; id: string } | null = null;
  private readonly downloadPhoto = btn('mt-cta-secondary mt-order-download');
  private readonly shareSheet = canShareFiles();
  /** Step 2 hands the photo to the share sheet where it takes files, otherwise downloads it. */
  private readonly handOff: PhotoHandOff = this.shareSheet ? 'share' : 'download';
  private flow: OrderFlow = INITIAL_ORDER_FLOW;
  /** A share sheet is open (a second `navigator.share` would be refused). */
  private sharing = false;
  private announceTimer = 0;
  private readonly onVisibility = (): void => this.onPageVisible();

  constructor(private readonly host: OrderPanelHost) {
    const w = words().order;
    this.root.dataset.view = 'order';
    this.root.dataset.state = 'idle';
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.title);
    const uid = Math.random().toString(36).slice(2, 8);

    const title = h('h3', 'mt-csec-title mt-order-title', w.title);

    // Photo (4:5) with « Reprendre »
    const shot = h('div', 'mt-order-shot');
    this.thumb.width = THUMB_W; this.thumb.height = THUMB_H;
    this.thumb.setAttribute('role', 'img');
    this.thumb.setAttribute('aria-label', w.photo);
    this.retake.setAttribute('aria-label', w.retakeAria);
    this.retake.append(icon('reset'), h('span', 'mt-order-retake-label', w.retake));
    this.retake.onclick = () => this.startCountdown();
    shot.append(this.thumb, this.retake);

    // Actions: WhatsApp (primary), then the photo, then the cart / the product page.
    const actions = h('div', 'mt-order-actions');
    const hint = this.hint;
    hint.textContent = w.whatsappHint;
    hint.id = `mt-order-hint-${uid}`;
    this.wa.target = '_blank';
    this.wa.rel = 'noopener noreferrer';
    this.wa.setAttribute('aria-describedby', hint.id);
    this.wa.append(icon('chat'), h('span', 'mt-cta-text', w.whatsapp), h('span', 'mt-order-sr', ` (${w.newTab})`));
    this.wa.addEventListener('click', () => this.onWhatsApp());

    // A110W step 2 (shown once WhatsApp was opened): step 1 done + « Rouvrir », then the photo.
    this.reopen.target = '_blank';
    this.reopen.rel = 'noopener noreferrer';
    this.reopen.append(document.createTextNode(w.reopen), h('span', 'mt-order-sr', `${w.reopenSr} (${w.newTab})`));
    this.reopen.addEventListener('click', () => { this.refresh(); });
    this.done.append(icon('check'), h('span', 'mt-order-done-text', w.step1Done), this.reopen);
    this.help.id = `mt-order-help-${uid}`;
    this.help.textContent = this.handOff === 'share' ? w.sendHint : w.saveHint;
    this.send.setAttribute('aria-describedby', this.help.id);
    this.send.append(icon(this.handOff === 'share' ? 'share' : 'save'), this.sendLabel);
    this.send.onclick = () => this.onSend();
    for (const el of [this.done, this.send, this.help]) el.hidden = true;

    this.saveLabel.textContent = this.shareSheet ? a204words().sharePhoto : w.save;
    this.save.append(icon(this.shareSheet ? 'share' : 'save'), this.saveLabel);
    this.save.onclick = () => void this.onSave();
    const row = h('div', 'mt-order-row');
    row.append(this.save);
    this.downloadPhoto.textContent = a204words().downloadPhoto;
    this.downloadPhoto.onclick = () => void this.photoFile().then(file => { if (file) downloadFile(file); else this.showMessage(w.photoError, 'photo'); });
    this.downloadPhoto.hidden = true;

    if (host.shop) {
      this.cart = btn('mt-cta-secondary mt-order-cart');
      this.cartLabel.textContent = w.addToCart;
      this.cart.append(icon('bag'), this.cartLabel);
      this.cart.onclick = () => void this.requestCart();
      this.cartDone = h('a', 'mt-cta-secondary mt-order-cart-done');
      this.cartDone.href = `${host.shop.root}cart`;
      this.cartDone.append(icon('check'), h('span', 'mt-order-label', w.added));
      this.cartDone.hidden = true;
      this.site = null;
      row.append(this.cart, this.cartDone);
    } else {
      this.cart = null; this.cartDone = null;
      this.site = h('a', 'mt-cta-secondary mt-order-site');
      this.site.target = '_blank';
      this.site.rel = 'noopener';
      this.site.append(icon('arrow'), h('span', 'mt-order-label', w.viewOnSite), h('span', 'mt-order-sr', ` (${w.newTab})`));
      row.append(this.site);
    }
    this.message.setAttribute('role', 'alert');
    this.message.hidden = true;
    // A201: « Code commande : MM-… · Fiche PDF » — one line under the choices (the phone panel keeps its height).
    this.pdfButton.append(icon('doc', 13), h('span', 'mt-order-label', w.pdfShort));
    this.pdfButton.title = w.pdfHint;
    this.pdfButton.setAttribute('aria-label', `${w.pdf} — ${w.pdfHint}`);
    this.pdfButton.onclick = () => void this.onPdf();
    this.codeLine.append(this.codeText, this.pdfButton);
    actions.append(this.wa, hint, this.done, this.send, this.downloadPhoto, this.help, row, this.message);

    const note = h('p', 'mt-order-note', a204words().privacy);
    this.priceNote.textContent = words().engrave.priceNote;
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    // Grid (see styles.css): mobile — photo left, title and specs right; desktop — title above.
    this.root.append(title, shot, this.specs, actions, note, this.status);
    this.clearThumb();
    this.renderStep();
    // Back from WhatsApp: step 2 is shown again and announced.
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /** The panel became visible (mobile panel or desktop tab): fresh specs and a new photo at once. */
  onShow(): void {
    this.visible = true;
    const s = this.refresh();
    track('tryon_order_open', { product_handle: s.handle, frame_id: s.frameId });
    void this.takePhoto();
  }

  onHide(): void {
    this.visible = false;
    this.cancelCountdown();
    window.clearTimeout(this.recaptureTimer);
    // Closing the panel (or another desktop tab) starts the WhatsApp order again at step 1.
    this.setFlow(orderFlow(this.flow, { type: 'hide' }));
  }

  /** Re-reads the current frame and choices into the spec list and the links. */
  refresh(): OrderSnapshot {
    const s = this.host.snapshot();
    const copy = orderCopy();
    this.specs.replaceChildren(...orderLines(s, copy).map(line => {
      const item = h('div', 'mt-order-spec');
      item.dataset.key = line.key;
      const dd = h('dd');
      if (line.key === 'engraving' && this.host.openEngrave) {
        // A110Z: the Gravure line reopens the engraving.
        const open = btn('mt-link mt-order-engrave', line.value);
        open.setAttribute('aria-label', `${line.value} — ${words().engrave.edit}`);
        open.onclick = () => this.host.openEngrave?.();
        dd.append(open);
      } else {
        dd.textContent = line.value;
      }
      item.append(h('dt', '', line.label), dd);
      return item;
    }));
    if (s.engraving) this.specs.append(this.priceNote);
    const code = this.code(s);
    this.codeText.textContent = `${copy.order.code}${copy.order.separator}${code}`;
    this.specs.append(this.codeLine);
    this.hint.textContent = s.engraving ? words().engrave.whatsappHint : words().order.whatsappHint;
    const url = productUrl(s.handle, this.host.shop);
    this.wa.href = whatsappUrl(whatsappMessage(s, copy, url, code));
    this.reopen.href = this.wa.href;
    if (this.site) {
      if (url) this.site.href = url; else this.site.removeAttribute('href');
      this.site.hidden = !url;
    }
    if (this.addedHandle !== null && (this.addedHandle !== this.fileKey(s) || this.addedPhoto !== this.photo)) this.resetCart();
    if (this.cart) this.cart.hidden = !s.handle || this.addedHandle === this.fileKey(s);
    return s;
  }

  /** A new model is on the face: the specs follow, and an open panel takes a new photo once the frame is shown. */
  frameChanged(): void {
    const s = this.refresh();
    // Another pair: the WhatsApp message of step 1 was about the previous one.
    this.setFlow(orderFlow(this.flow, { type: 'frame', frameId: s.frameId }));
    window.clearTimeout(this.recaptureTimer);
    this.file = null;
    this.pdf = null;
    if (this.visible) {
      this.recaptureTimer = window.setTimeout(() => { if (this.visible && !this.countdownEl) void this.takePhoto(); }, RECAPTURE_AFTER_SWITCH_MS);
    } else {
      this.dropPhoto();
      this.clearThumb();
      this.setState('idle');
    }
  }

  destroy(): void {
    this.consentDialog?.cancel(); this.uploadAbort.abort();
    this.onHide();
    this.captureToken++;
    this.dropPhoto();
    this.file = null;
    window.clearTimeout(this.announceTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** A201: the order code of these choices (WhatsApp, photo, order sheet, cart). */
  private code(s: OrderSnapshot = this.host.snapshot()): string {
    return orderCode(s, this.salt);
  }

  // ------------------------------------------------------------------ photo

  private setState(state: PanelState): void {
    this.root.dataset.state = state;
    this.thumb.setAttribute('aria-busy', String(state === 'capturing' || state === 'countdown'));
    this.renderSend();
  }

  private capturing(): boolean {
    const state = this.root.dataset.state;
    return state === 'capturing' || state === 'countdown';
  }

  private clearThumb(): void {
    this.thumb.getContext('2d')?.clearRect(0, 0, THUMB_W, THUMB_H);
  }

  /** A110Y: forgets the current photo and gives its canvas back (after the JPEG in progress, if any). */
  private dropPhoto(next: HTMLCanvasElement | null = null): void {
    const old = this.photo;
    this.photo = next;
    if (old && old !== next) releaseCanvas(old, this.file?.photo === old && !this.file.done ? this.file.promise : undefined);
  }

  private async takePhoto(): Promise<void> {
    const token = ++this.captureToken;
    this.setState('capturing');
    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = await this.host.capture();
    } catch {
      canvas = null;
    }
    if (token !== this.captureToken) return;
    if (!canvas || !canvas.width || !canvas.height) {
      this.setState(this.photo ? 'ready' : 'error');
      if (!this.photo) this.showMessage(words().order.photoError, 'photo');
      return;
    }
    if (this.messageKind === 'photo') this.hideMessage();
    this.dropPhoto(canvas);
    if (this.addedHandle !== null && this.addedPhoto !== canvas) this.resetCart();
    this.photoFocus = this.host.focus?.() ?? null;
    this.file = null;
    this.pdf = null;
    // A new photo keeps step 1 done: step 2 sends this one.
    this.setFlow(orderFlow(this.flow, { type: 'photo' }));
    this.drawThumb();
    this.setState('ready');
    window.clearTimeout(this.announceTimer);
    this.status.textContent = words().order.taken;
    // Prepared now so that « Partager » / step 2 can open the share sheet within the tap.
    void this.photoFile().then(() => this.pdfFile());
  }

  private drawThumb(): void {
    const photo = this.photo;
    const ctx = this.thumb.getContext('2d');
    if (!photo || !ctx) return;
    const crop = coverRect(photo.width, photo.height, THUMB_W, THUMB_H, this.photoFocus ?? undefined);
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, THUMB_W, THUMB_H);
    ctx.drawImage(photo, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, THUMB_W, THUMB_H);
  }

  /** 3-2-1 over the camera, then a new photo. With reduced motion the numbers still count, without animation. */
  private startCountdown(): void {
    if (this.countdownEl) return;
    window.clearTimeout(this.recaptureTimer);
    const el = h('div', 'mt-order-count');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'assertive');
    const num = h('span', 'mt-order-count-num');
    el.append(num);
    this.host.stage.append(el);
    this.countdownEl = el;
    this.setState('countdown');
    // Deadline-based: each number at start + k s, so a busy frame does not stretch the countdown.
    const start = performance.now();
    let k = 0;
    const step = () => {
      if (this.countdownEl !== el) return;
      if (k === 3) {
        this.cancelCountdown();
        void this.takePhoto();
        return;
      }
      num.textContent = String(3 - k);
      num.classList.remove('mt-order-count-num--tick');
      void num.offsetWidth; // restart the animation
      num.classList.add('mt-order-count-num--tick');
      k++;
      this.countdownTimer = window.setTimeout(step, Math.max(0, start + k * COUNTDOWN_STEP_MS - performance.now()));
    };
    step();
  }

  private cancelCountdown(): void {
    window.clearTimeout(this.countdownTimer);
    if (!this.countdownEl) return;
    this.countdownEl.remove();
    this.countdownEl = null;
    if (this.root.dataset.state === 'countdown') this.setState(this.photo ? 'ready' : 'idle');
  }

  /** Cache key of the JPEG: the choices it shows (and the UI language of its band). */
  private fileKey(s: OrderSnapshot = this.host.snapshot()): string {
    return JSON.stringify([s, orderCopy().order.title, productUrl(s.handle, this.host.shop)]);
  }

  /** The JPEG of the current photo and specs (cached until either changes). */
  private photoFile(): Promise<File | null> {
    const photo = this.photo;
    if (!photo) return Promise.resolve(null);
    const s = this.host.snapshot();
    const copy = orderCopy();
    const url = productUrl(s.handle, this.host.shop);
    const key = this.fileKey(s);
    if (this.file && this.file.key === key && this.file.photo === photo && !(this.file.done && !this.file.ready)) return this.file.promise;
    const family = (this.root.isConnected ? getComputedStyle(this.root).fontFamily : '') || FONT_FALLBACK;
    const entry: NonNullable<OrderPanel['file']> = { key, photo, promise: Promise.resolve(null), ready: null, done: false };
    // A202: with a changed engraving, the four faces of the temples follow the photo (see `engravingSheet`).
    const sheet = this.host.workshopRequired?.() && this.host.engravingSheet ? this.host.engravingSheet(family) : null;
    this.host.busy?.(true);
    entry.promise = composeOrderPhoto(photo, this.photoFocus, photoText(s, copy, url, this.code(s)), family, sheet)
      .then(blob => (blob ? new File([blob], photoFileName(s.frameId), { type: 'image/jpeg' }) : null))
      .catch(() => null)
      .finally(() => this.host.busy?.(false))
      .then(file => {
        entry.ready = file;
        entry.done = true;
        if (this.file === entry) this.renderSend();
        return file;
      });
    this.file = entry;
    return entry.promise;
  }

  /** The JPEG, synchronously, when it is composed for the current photo and choices (else null). */
  private readyFile(): File | null {
    const f = this.file;
    if (!f || !f.ready || f.photo !== this.photo) return null;
    return f.key === this.fileKey() ? f.ready : null;
  }

  /**
   * A201: the order sheet (PDF) of the current photo and choices — page 1 the order, page 2 the four faces of the
   * temples when the frame can be engraved. Cached like the JPEG.
   */
  private pdfFile(): Promise<File | null> {
    const photo = this.photo;
    if (!photo) return Promise.resolve(null);
    const s = this.host.snapshot();
    const key = this.fileKey(s);
    if (this.pdf && this.pdf.key === key && this.pdf.photo === photo && !(this.pdf.done && !this.pdf.ready)) return this.pdf.promise;
    const copy = orderCopy();
    const code = this.code(s);
    const url = productUrl(s.handle, this.host.shop);
    const family = (this.root.isConnected ? getComputedStyle(this.root).fontFamily : '') || FONT_FALLBACK;
    const focus = this.photoFocus;
    const entry: NonNullable<OrderPanel['pdf']> = { key, photo, promise: Promise.resolve(null), ready: null, done: false };
    const date = new Intl.DateTimeFormat(getLang(), { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
    entry.promise = (async () => {
      // Step by step, giving the frame back between steps (the camera and the 3D keep their pace), and telling
      // the try-on that a long frame is ours (its watchdog must not take it for a frozen tracker).
      const timing: Record<string, number> = {};
      let t = performance.now();
      const lap = (name: string) => { const now = performance.now(); timing[name] = Math.round(now - t); t = now; };
      this.host.busy?.(true);
      try {
        await loadFonts(family); lap('fonts');
        await nextFrame();
        const first = renderOrderPage({ photo, focus, snapshot: s, copy, code, url, family, date }); lap('page1');
        await nextFrame();
        const second = this.host.workshopPage ? await this.host.workshopPage(family, code) : null; lap('page2');
        if (this.host.workshopRequired?.() && !second) throw new Error('Incomplete workshop PDF');
        await nextFrame();
        const blob = await composeOrderPdf([first, second], { title: `Maison Mata — ${frameTitle(s)} — ${code}`, subject: copy.order.pdfTitle }); lap('encode');
        (window as Window & { __mataPdfTiming?: Record<string, number> }).__mataPdfTiming = timing;
        return blob ? new File([blob], pdfFileName(s.frameId, code), { type: 'application/pdf' }) : null;
      } finally {
        this.host.busy?.(false);
      }
    })()
      .catch(() => null)
      .then(file => {
        entry.ready = file;
        entry.done = true;
        if (this.pdf === entry) this.renderSend();
        return file;
      });
    this.pdf = entry;
    return entry.promise;
  }

  /** The order sheet, synchronously, when it is ready for the current photo and choices (else null). */
  private readyPdf(): File | null {
    const f = this.pdf;
    if (!f || !f.ready || f.photo !== this.photo) return null;
    return f.key === this.fileKey() ? f.ready : null;
  }

  /** The JPEG of the current photo could not be composed. */
  private fileFailed(): boolean {
    const f = this.file;
    return !!f && f.done && !f.ready && f.photo === this.photo;
  }

  // ------------------------------------------------------------------ WhatsApp in two steps (A110W)

  private setFlow(next: OrderFlow): void {
    if (next === this.flow) return;
    this.flow = next;
    this.renderStep();
  }

  /** Step 1 (« Commander sur WhatsApp ») or step 2 (step 1 done, « Envoyer la photo »). */
  private renderStep(): void {
    const photoStep = this.flow.step === 'photo';
    this.root.dataset.step = this.flow.step;
    this.wa.hidden = photoStep;
    this.hint.hidden = photoStep;
    // Step 2 is the photo action: no second photo button next to it.
    this.save.hidden = photoStep;
    this.done.hidden = !photoStep;
    this.send.hidden = !photoStep;
    this.help.hidden = !photoStep;
    this.renderSend();
  }

  /** Label and availability of the step 2 button; asks for the JPEG when it is missing. */
  private renderSend(): void {
    if (this.flow.step !== 'photo') { this.downloadPhoto.hidden = true; return; }
    const w = a204words();
    const image = this.readyFile(), pdf = this.readyPdf();
    const ready = Boolean(image && pdf && !this.capturing());
    const failed = this.fileFailed() || Boolean(this.pdf?.done && !this.pdf.ready);
    this.sendLabel.textContent = ready ? w.sendBoth : failed ? w.retry : w.preparing;
    this.send.setAttribute('aria-disabled', String(!ready && !failed));
    this.send.setAttribute('aria-busy', String(!ready && !failed));
    const canSend = Boolean(image && pdf && canShareFiles2(image, pdf));
    this.downloadPhoto.hidden = !ready || canSend;
    this.send.hidden = ready && !canSend;
    this.help.textContent = canSend ? w.waHelp : w.fallback;
    if (!ready && !failed && this.photo && !this.capturing()) {
      void this.photoFile().then(() => this.pdfFile());
    }
  }

  /** Screen readers hear that step 2 is there (polite, after the layout change). */
  private announceStep2(): void {
    const w = words().order;
    const text = `${w.step1Done}. ${a204words().sendBoth}. ${a204words().waHelp}`;
    window.clearTimeout(this.announceTimer);
    // Emptied first so that the same text is announced again on return from WhatsApp.
    this.status.textContent = '';
    this.announceTimer = window.setTimeout(() => { this.status.textContent = text; }, 80);
  }

  /** Focus would stay on the hidden WhatsApp link (or fall to the page): step 2 takes it. */
  private focusStep2(): void {
    const active = document.activeElement;
    if (!active || active === document.body || active === this.wa) (this.send.hidden ? this.downloadPhoto : this.send).focus({ preventScroll: true });
  }

  /** Back from WhatsApp (or from the wa.me tab on desktop). */
  private onPageVisible(): void {
    if (document.visibilityState !== 'visible' || !this.visible || this.flow.step !== 'photo') return;
    this.renderStep();
    this.announceStep2();
    this.focusStep2();
  }

  // ------------------------------------------------------------------ actions

  private eventParams(s: OrderSnapshot): TryOnEventParams {
    const params: TryOnEventParams = { product_handle: s.handle, frame_id: s.frameId };
    if (s.materials) { params.acetate = s.materials.acetateId; params.lens = s.materials.lensId; }
    return params;
  }

  private onWhatsApp(): void {
    // The link follows the latest choices even if something changed since the panel opened.
    const s = this.refresh();
    track('tryon_order_whatsapp', this.eventParams(s));
    // Step 2 at once (the link still opens: hiding it does not cancel its navigation).
    const hadFocus = document.activeElement === this.wa;
    this.setFlow(orderFlow(this.flow, { type: 'whatsapp', frameId: s.frameId }));
    this.announceStep2();
    if (hadFocus) this.send.focus({ preventScroll: true });
  }

  /**
   * Step 2. Synchronous up to `navigator.share`: iOS opens the share sheet only
   * within the tap, so the JPEG must already exist (the button waits otherwise).
   */
  private onSend(): void {
    if (this.sharing) return;
    const photo = this.capturing() ? null : this.readyFile(), pdf = this.readyPdf();
    if (!photo || !pdf) {
      if (this.fileFailed() || (this.pdf?.done && !this.pdf.ready)) { this.file = null; this.pdf = null; }
      if (!this.photo && !this.capturing()) void this.takePhoto();
      else void this.photoFile().then(() => this.pdfFile());
      this.renderSend(); return;
    }
    if (!canShareFiles2(photo, pdf)) { this.showWhatsAppFallback(); return; }
    this.sharing = true;
    let share: Promise<void>;
    try { share = navigator.share({ files: [photo, pdf], text: photoCaption(this.host.snapshot(), orderCopy()) }); }
    catch (error) { share = Promise.reject(error); }
    share.catch(error => { if (errorName(error) !== 'AbortError') this.showWhatsAppFallback(); })
      .finally(() => { this.sharing = false; });
  }

  private showWhatsAppFallback(): void {
    this.downloadPhoto.hidden = false; this.help.hidden = false;
    this.help.textContent = a204words().fallback;
    this.pdfButton.focus({ preventScroll: true });
  }

  private async onSave(): Promise<void> {
    if (this.save.getAttribute('aria-busy') === 'true') return;
    this.hideMessage();
    this.save.setAttribute('aria-busy', 'true');
    if (!this.photo) await this.takePhoto();
    const s = this.host.snapshot();
    const file = await this.photoFile();
    this.save.removeAttribute('aria-busy');
    if (!file) { this.showMessage(words().order.photoError, 'photo'); return; }
    const url = productUrl(s.handle, this.host.shop);
    const title = `Maison Mata — ${frameTitle(s)}`;
    const method = await deliverPhoto(file, title, url ? `${title}\n${url}` : title);
    if (method) track('tryon_order_share', { ...this.eventParams(s), method });
  }

  /** A201 « Fiche commande (PDF) » : the share sheet on phones, a download elsewhere. */
  private async onPdf(): Promise<void> {
    if (this.pdfButton.getAttribute('aria-busy') === 'true') return;
    this.hideMessage();
    const ready = this.readyPdf();
    let file = ready;
    if (!file) {
      this.pdfButton.setAttribute('aria-busy', 'true');
      if (!this.photo) await this.takePhoto();
      file = await this.pdfFile();
      this.pdfButton.removeAttribute('aria-busy');
    }
    if (!file) { this.showMessage(words().order.photoError, 'photo'); return; }
    downloadFile(file);
    track('tryon_order_pdf', { ...this.eventParams(this.host.snapshot()), method: 'download' });
  }

  private async requestCart(): Promise<void> {
    if (this.consentDialog || this.cart?.getAttribute('aria-busy') === 'true') return;
    if (!this.host.dossierUrl) { this.showMessage(a204words().unavailable, 'cart'); return; }
    const s = this.refresh(), key = this.fileKey(s), photo = this.photo;
    this.consentDialog = orderConsent(this.host.stage.closest<HTMLElement>('.mt-root') ?? this.host.stage,
      `${frameTitle(s)} · ${this.code(s)}`, this.host.retentionDays ?? 90);
    const agreed = await this.consentDialog.promise; this.consentDialog = null;
    if (!agreed || this.uploadAbort.signal.aborted) return;
    if (key !== this.fileKey() || photo !== this.photo) { this.showMessage(a204words().changed, 'cart'); return; }
    await this.onCart();
  }

  private async onCart(): Promise<void> {
    const cart = this.cart;
    const shop = this.host.shop;
    if (!cart || !shop || cart.getAttribute('aria-busy') === 'true') return;
    const s = this.refresh();
    const copy = orderCopy();
    this.hideMessage();
    const hadFocus = document.activeElement === cart;
    cart.disabled = true;
    cart.setAttribute('aria-busy', 'true');
    this.cartLabel.textContent = copy.order.adding;
    // A202: a Signature in Sunset lenses — the supplement (« Plano Lens · Special Tint », Rp 990.000) goes in with it.
    const sunsetSupplement = Boolean(s.materials?.lensOnly && isSunsetId(s.materials.lensId));
    const extras = sunsetSupplement ? [{ ...SIGNATURE_SUNSET_SUPPLEMENT_PRODUCT, properties: supplementProperties(s, copy, this.code(s)) }] : [];
    const key = this.fileKey(s);
    let result: Awaited<ReturnType<typeof addToCart>>;
    try {
      if (!this.photo) await this.takePhoto();
      const photo = this.photo;
      const [image, pdf] = await Promise.all([this.photoFile(), this.pdfFile()]);
      if (!photo || !image || !pdf || !this.host.dossierUrl || key !== this.fileKey() || photo !== this.photo) throw new Error('Order changed or incomplete');
      if (!this.dossier || this.dossier.key !== key || this.dossier.photo !== photo) this.dossier = { key, photo, id: crypto.randomUUID() };
      const id = await uploadDossier(this.host.dossierUrl, this.dossier.id, image, pdf, s, this.code(s), this.uploadAbort.signal);
      if (key !== this.fileKey() || photo !== this.photo || this.uploadAbort.signal.aborted) throw new Error('Order changed');
      result = await addToCart({ root: shop.root, handle: s.handle, colorway: s.colorway,
        properties: { ...cartProperties(s, copy, this.code(s)), _vto_dossier: id }, extras: extras.map(extra => ({ ...extra, properties: { ...extra.properties, _vto_dossier: id } })) });
      if (result.ok) this.addedPhoto = photo;
    } catch { result = { ok: false, code: 'dossier_unavailable' }; }
    cart.disabled = false;
    cart.removeAttribute('aria-busy');
    this.cartLabel.textContent = copy.order.addToCart;
    if (!this.root.isConnected) return;
    if (result.ok) {
      track('tryon_order_cart', { ...this.eventParams(s), ...(result.supplement ? { supplement: result.supplement } : {}) });
      this.addedHandle = key;
      cart.hidden = true;
      if (this.cartDone) {
        this.cartDone.hidden = false;
        if (hadFocus || document.activeElement === document.body) this.cartDone.focus({ preventScroll: true });
      }
      this.refresh();
    } else {
      track('tryon_order_cart_error', { ...this.eventParams(s), code: result.code });
      this.showMessage(copy.order.cartError, 'cart');
      if (hadFocus) cart.focus({ preventScroll: true });
    }
  }

  private resetCart(): void {
    this.addedHandle = null; this.addedPhoto = null;
    if (this.cartDone) this.cartDone.hidden = true;
    if (this.cart) this.cart.hidden = false;
  }

  private showMessage(text: string, kind: 'photo' | 'cart'): void {
    this.messageKind = kind;
    this.message.textContent = text;
    this.message.hidden = false;
  }

  private hideMessage(): void {
    this.messageKind = null;
    this.message.hidden = true;
    this.message.textContent = '';
  }
}

/**
 * Hands the photo to the customer: the system share sheet when it takes files
 * (phones), otherwise a download. A dismissed share sheet is not an error.
 */
async function deliverPhoto(file: File, title: string, text: string): Promise<'share' | 'download' | null> {
  if (canShareFile(file)) {
    try {
      await navigator.share({ files: [file], title, text });
      return 'share';
    } catch (err) {
      if (errorName(err) === 'AbortError') return null;
      // Share refused (e.g. the tap's activation expired): fall back to a download.
    }
  }
  downloadFile(file);
  return 'download';
}

/** Saves the photo through a temporary link (the browser's download). */
function downloadFile(file: File): void {
  const href = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = href;
  a.download = file.name;
  a.rel = 'noopener';
  a.hidden = true;
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
